// Pure helpers for the cross-region sync flow that prepare-dashboard owns.
//
// The profile's `organisations[].region` is the single source of truth for the
// cluster a user's data lives in. The login dropdown only picks which auth host
// to authenticate against. prepare-dashboard fetches the profile, derives the
// region, and — when it differs from the region the app currently points at —
// persists the new region and reloads ONCE so every module-load-time URL
// constant (AUTH_URL, DOC2API_URL, …) rebuilds against the correct prefix.

import euToUsAnimation from 'src/assets/reroute-user-eu-to-us.svg';
import usToEuAnimation from 'src/assets/reroute-user-us-to-eu.svg';
import type { ApiRegion } from 'src/pages/api-base-url';

export const MAX_SYNC_ATTEMPTS = 6;

// Cumulative backoff: 1 + 2 + 4 + 8 + 16 + 16 = 47s of waiting across 6 retries.
export function syncRetryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** (attempt - 1), 16000);
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isTokenAuthError(error: unknown): boolean {
  const e = error as { response?: { status?: number }; status?: number } | undefined;
  const status = e?.response?.status ?? e?.status;
  return status === 401 || status === 403;
}

interface ProfileOrg {
  is_default?: boolean;
  region?: string;
}

/**
 * The region of the user's default organisation (or the first one when none is
 * flagged default). Returns null when no organisation carries an explicit
 * 'us'/'eu' region — the caller then trusts the current region instead of
 * forcing a reroute. (Forcing a missing region to 'eu' was the DOCB-12864 root
 * cause: it rerouted correctly-US users toward EU.)
 */
export function regionFromOrgs(orgs: ProfileOrg[]): ApiRegion | null {
  if (!orgs.length) return null;
  const defaultOrg = orgs.find((o) => o?.is_default) ?? orgs[0];
  if (defaultOrg?.region === 'us') return 'us';
  if (defaultOrg?.region === 'eu') return 'eu';
  return null;
}

/**
 * The region to switch to, or null to stay put. A switch is needed only when
 * the profile reports a real region that differs from where the app currently
 * points.
 */
export function regionSwitchTarget(
  currentRegion: ApiRegion,
  profileRegion: ApiRegion | null,
): ApiRegion | null {
  if (!profileRegion) return null;
  return profileRegion === currentRegion ? null : profileRegion;
}

/** The directional transition animation for a `from → to` region switch. */
export function switchAnimationAsset(from: ApiRegion, to: ApiRegion): string {
  if (from === 'us' && to === 'eu') return usToEuAnimation;
  return euToUsAnimation;
}

/**
 * Side-effect-free readiness probe for the post-reload retry loop. IMPORTANT:
 * userApiService.getProfile() swallows 401 into an empty {} (it never throws on
 * auth errors), so we can't rely on a thrown error to detect "token not yet
 * accepted by the target region". Instead we treat a profile with at least one
 * organisation as ready, and an empty/orgless/failed response as not-ready.
 */
export async function probeProfileReady(): Promise<boolean> {
  try {
    const { userApiService } = await import('src/services');
    const profile = await userApiService.getProfile();
    const orgs = Array.isArray(profile?.organisations)
      ? (profile.organisations as ProfileOrg[])
      : [];
    return orgs.length > 0;
  } catch {
    return false;
  }
}
